import { Row } from "@tanstack/react-table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { DataTable } from "@/components/ui/data-table";
import { User, userColumns } from "@/lib/columns";
import { axiosPrivate } from "@/api/axios";
import { fetchAllUsers } from "@/api/users";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingTable } from "@/components/ui/LoadingSpinner";
import UserDetailDialog from "../user/UserDetailDialog";

function InfluencerListPage() {
  const queryClient = useQueryClient();
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users", "influencers"],
    queryFn: async () => {
      const users = await fetchAllUsers();
      return users.filter((user) => user.role === "influencer");
    },
    placeholderData: [],
  });

  const removeInfluencerMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await axiosPrivate.put(`/users/${userId}`, { role: "user" });
      return { userId, res };
    },
    onSuccess: ({ userId }) => {
      toast.success(`User #${userId} has been removed from Influencers`);
      queryClient.invalidateQueries({ queryKey: ["users", "influencers"] });
    },
    onError: () => {
      toast.error("Something went wrong on the server side");
    },
  });

  const actions = {
    id: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<User> }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(row.original);
                setDetailOpen(true);
              }}
            >
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => removeInfluencerMutation.mutate(row.original.id)}
              disabled={removeInfluencerMutation.isPending}
            >
              Remove Influencer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };

  const columns = React.useMemo(() => [...userColumns, actions], [removeInfluencerMutation.isPending]);

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Influencer Management</h1>
      <p>Manage all influencers in one place. View, filter, and monitor influencer users.</p>
      {isLoading ? (
        <LoadingTable message="Loading influencers..." />
      ) : (
        <DataTable
          columns={columns}
          data={data ?? []}
          placeholder="Filter Influencers"
          filterColumns={["username", "phone_number", "country"]}
        />
      )}
      <UserDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        user={selectedUser}
      />
    </div>
  );
}

export default InfluencerListPage;
